import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BadgeCheck, FileText, ShieldCheck, ScrollText } from "lucide-react";
import BackButton from "../../components/BackButton";
import { Alert } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import {
  SNK_CHECKBOXES,
  SNK_CONSENT_STATEMENT,
  SNK_EFFECTIVE_DATE,
  SNK_SECTIONS,
  SNK_SUMMARY,
  SNK_VERSION,
  loadSnkConsent,
  saveSnkConsent,
} from "../../data/snkKyc";

export default function WorkerKycTerms() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [scrolledFull, setScrolledFull] = useState(false);
  const [checks, setChecks] = useState({ data: false, truth: false });
  const [already] = useState(() => loadSnkConsent(user?.id));

  const allChecked = checks.data && checks.truth;
  const canContinue = scrolledFull && allChecked;

  const progress = useMemo(() => {
    const done = Object.values(checks).filter(Boolean).length;
    return { done, total: SNK_CHECKBOXES.length };
  }, [checks]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
      if (nearBottom) setScrolledFull(true);
    };
    el.addEventListener("scroll", onScroll);
    // Konten pendek di layar besar bisa langsung terbaca penuh.
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const toggle = (id) => setChecks((c) => ({ ...c, [id]: !c[id] }));

  const handleContinue = () => {
    if (!canContinue) return;
    saveSnkConsent(user?.id);
    navigate("/worker/profile#kyc", { state: { snkAccepted: true, snkVersion: SNK_VERSION } });
  };

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container max-w-lg space-y-6">
        <BackButton to="/worker/profile" />
        <div>
          <p className="chip bg-brand-50 text-brand-600 mb-2">
            <ScrollText className="h-4 w-4" /> Wajib dibaca sebelum KYC
          </p>
          <h1 className="text-brand-800">Syarat & Ketentuan KYC Worker</h1>
          <p className="text-brand-400 mt-1 text-sm">
            Versi {SNK_VERSION} • Berlaku {SNK_EFFECTIVE_DATE} • Harap baca sampai selesai lalu centang persetujuan.
          </p>
        </div>

        {already && (
          <Alert kind="info" testid="snk-already">
            Anda pernah menyetujui SNK versi {already.version} pada{" "}
            {new Date(already.acceptedAt).toLocaleString("id-ID")}. Membaca ulang & mencentang kembali akan memperbarui
            bukti persetujuan.
          </Alert>
        )}

        {/* Ringkasan 8 poin (UU PDP Pasal 21) */}
        <div className="card p-6">
          <h2 className="text-brand-800 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-success-500" /> Ringkasan penting
          </h2>
          <div className="mt-3 grid gap-2">
            {SNK_SUMMARY.map((s) => (
              <div key={s.title} className="rounded-2xl bg-brand-50 px-4 py-3">
                <p className="font-bold text-brand-800 text-sm">{s.title}</p>
                <p className="text-sm text-brand-600 mt-0.5">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Dokumen lengkap — wajib scroll */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-brand-800 flex items-center gap-2">
              <FileText className="h-5 w-5 text-brand-500" /> Isi ketentuan
            </h2>
            <span
              className={`chip ${scrolledFull ? "bg-success-50 text-success-600" : "bg-amber-50 text-amber-600"}`}
              data-testid="snk-scroll-status"
            >
              {scrolledFull ? "Sudah dibaca penuh" : "Scroll sampai bawah"}
            </span>
          </div>
          <div
            ref={scrollRef}
            data-testid="snk-scroll-area"
            className="mt-3 h-96 overflow-y-auto rounded-2xl border-2 border-brand-100 p-4 space-y-4 bg-white"
          >
            {SNK_SECTIONS.map((s) => (
              <section key={s.no} data-testid={`snk-section-${s.no}`}>
                <p className="font-bold text-brand-800 text-sm">
                  {s.no}. {s.title}
                </p>
                {s.body.map((p, i) => (
                  <p key={i} className="text-sm text-brand-600 mt-1 leading-relaxed">
                    {p}
                  </p>
                ))}
              </section>
            ))}
            <div className="rounded-2xl bg-brand-700 text-white p-4">
              <p className="font-bold text-sm">Pernyataan persetujuan (Pasal 27)</p>
              <p className="text-sm mt-1 leading-relaxed">“{SNK_CONSENT_STATEMENT}”</p>
            </div>
          </div>
          {!scrolledFull && (
            <p className="text-xs text-amber-600 mt-2">
              Gulir kotak di atas sampai bagian Pernyataan Persetujuan untuk membuka tombol persetujuan.
            </p>
          )}
        </div>

        {/* Checkbox persetujuan */}
        <div className="card p-6 space-y-3" data-testid="snk-consent-box">
          <p className="font-bold text-brand-800 text-sm">
            Persetujuan ({progress.done}/{progress.total})
          </p>
          {!scrolledFull && (
            <Alert kind="info" testid="snk-locked-note">
              Centang aktif setelah Anda membaca seluruh ketentuan di atas.
            </Alert>
          )}
          {SNK_CHECKBOXES.map((c) => (
            <label
              key={c.id}
              className={`flex items-start gap-3 rounded-2xl border-2 p-3 text-sm cursor-pointer transition ${
                checks[c.id] ? "border-success-400 bg-success-50" : "border-brand-100 bg-white"
              } ${scrolledFull ? "" : "opacity-60"}`}
            >
              <input
                type="checkbox"
                checked={checks[c.id]}
                disabled={!scrolledFull}
                onChange={() => toggle(c.id)}
                className="mt-1 h-4 w-4 accent-emerald-600"
                data-testid={`snk-check-${c.id}`}
              />
              <span className="text-brand-700">{c.label}</span>
            </label>
          ))}

          <button
            onClick={handleContinue}
            disabled={!canContinue}
            className="btn-primary w-full disabled:opacity-50"
            data-testid="snk-continue-btn"
          >
            <BadgeCheck className="h-4 w-4" />
            {canContinue ? "Saya Setuju — Lanjutkan Upload KYC" : "Baca & centang untuk lanjut"}
          </button>
          <Link to="/worker/profile" className="btn-ghost w-full text-center block">
            Kembali tanpa menyetujui
          </Link>
          <p className="text-xs text-brand-400 text-center">
            Persetujuan dicatat (versi {SNK_VERSION}, waktu & akun Anda) sebagai bukti sah sesuai UU PDP.
          </p>
        </div>

        <div className="text-center">
          <Link to="/worker/profile" className="text-sm text-brand-500 inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Kembali ke Profil
          </Link>
        </div>
      </div>
    </div>
  );
}
